import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ChevronDownIcon,
  FileAttachment01Icon as FileIcon,
  FilterLinesIcon,
  PlusIcon,
} from '@langchain/untitled-ui-icons';
import { Button, Input, LinearProgress } from '@mui/joy';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import {
  Content as DialogContent,
  DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
} from '@radix-ui/react-dialog';

import dayjs from 'dayjs';
import { ReactNode, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import LoadingSlimIcon from '@/Pages/Graph/icons/LoadingSlimIcon.svg?react';
import { CodeData } from '@/components/Code/Code';
import EmptyState from '@/components/EmptyState';
import { MultipleArrayInput } from '@/components/MultipleArrayInput';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { IconButton, Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';
import { toRelativeTime } from '@/utils/to-relative-time';

import {
  useGraphMemoryDelete,
  useGraphMemoryNamespaces,
  useGraphMemoryPut,
  useGraphMemorySearch,
} from '../../api/useGraphSwr';
import {
  filterItems,
  isEmptyKeyFilter,
  isEmptyValueFilter,
} from './GraphMemoryEditor.utils';

export function File(props: {
  name: string;
  onClick: () => void;
  selected?: boolean;
  date: string | undefined;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex flex-row items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm transition-colors hover:bg-tertiary',
        props.selected && 'border-primary'
      )}
      onClick={props.onClick}
    >
      <FileIcon className="size-4" />
      <span className="flex-grow text-left">{props.name}</span>
      {props.date && (
        <Tooltip title={dayjs(props.date).format('YYYY/MM/DD HH:mm:ss')}>
          <span className="mr-0.5 text-right text-xs text-tertiary">
            {toRelativeTime(props.date)}
          </span>
        </Tooltip>
      )}
    </button>
  );
}

export type FolderContents = {
  folders: { [key: string]: FolderContents };
  items: EditItem[];
};

type View =
  | { type: 'create'; value: Partial<EditItem> }
  | { type: 'edit'; value: EditItem };

const isFileSelected = (view: View, item: EditItem) =>
  view.type === 'edit' &&
  view.value.namespace?.join('/') === item.namespace.join('/') &&
  view.value.key === item.key;

const FolderTrigger = (props: {
  name: string;
  count: number | undefined;
  onClick: () => void;
  loading?: boolean;
  actions?: ReactNode;
}) => {
  return (
    <CollapsibleTrigger className="group/trigger flex w-full flex-row items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm transition-colors hover:bg-tertiary">
      {props.loading ? (
        <LoadingSlimIcon className="size-4 animate-spin" />
      ) : (
        <ChevronDownIcon className="size-4 transition-transform group-data-[state=closed]/trigger:-rotate-90" />
      )}
      <span className="flex flex-grow items-center gap-1 text-left">
        <span>{props.name}</span>
        {!!props.count && (
          <span className="rounded bg-secondary px-1 text-xxs tabular-nums text-tertiary">
            {props.count}
          </span>
        )}
      </span>

      <Tooltip title="Create new item">
        <span
          className="cursor-pointer opacity-0 transition-opacity group-hover/trigger:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            props.onClick();
          }}
        >
          <PlusIcon className="size-4" />
        </span>
      </Tooltip>

      {props.actions}
    </CollapsibleTrigger>
  );
};

interface EditItem {
  key: string;
  value: Record<string, unknown>;
  namespace: string[];

  created_at?: string;
  updated_at?: string;
}

function GraphMemoryForm(props: {
  initialValues?: Partial<
    EditItem & { createdAt?: string; updatedAt?: string }
  >;
  onClose: () => void;
  onSuccess: (type: EditItem | null) => void;
}) {
  'use no memo';
  const form = useForm<EditItem>({
    defaultValues: {
      ...props.initialValues,
      value: props.initialValues?.value ?? {},
    },
  });

  const putItem = useGraphMemoryPut(props.initialValues, {
    onSuccess: (arg) => {
      form.reset(arg);
      props.onSuccess(arg);
    },
  });

  const deleteItem = useGraphMemoryDelete({
    onSuccess: () => props.onSuccess?.(null),
  });

  return (
    <form
      onSubmit={form.handleSubmit((data) => putItem.trigger(data))}
      className="flex flex-grow flex-col gap-4 p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold tracking-tighter">
          {props.initialValues?.key && props.initialValues?.namespace
            ? 'Edit item'
            : 'Add new item'}
        </h2>
        <button
          type="button"
          onClick={() => props.onClose()}
          className="rounded-md p-1 text-ls-black outline-none transition-all hover:bg-secondary-hover"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-tertiary">Key</span>
        <Input
          {...form.register('key', { required: 'Key must not be empty' })}
          sx={{ background: 'transparent' }}
        />
        {form.formState.errors.key && (
          <div className="text-xs text-error">
            {form.formState.errors.key.message}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-tertiary">Namespace</span>
        <Controller
          name="namespace"
          control={form.control}
          rules={{
            validate: {
              notEmpty: (value) =>
                (value && value?.length > 0) || 'Namespace must not be empty',
              noPeriod: (value) =>
                !value.some((x) => x.includes('.')) ||
                'Namespace labels cannot contain periods',
              noEmptyString: (value) =>
                !value?.includes('') || 'Namespace labels cannot be empty',
            },
          }}
          render={({ field: { onChange, value } }) => (
            <MultipleArrayInput
              className="min-h-[42px]"
              placeholder="Root..."
              onChange={onChange}
              value={value}
              separators={/[,\s]/}
            />
          )}
        />
        {form.formState.errors.namespace && (
          <div className="text-xs text-error">
            {form.formState.errors.namespace.message}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-tertiary">Value</span>
        <Controller
          name="value"
          control={form.control}
          render={({ field: { onChange, value } }) => (
            <div className="overflow-hidden rounded-md border border-secondary">
              <CodeData value={value} onChange={onChange} />
            </div>
          )}
        />
      </div>

      <div className="flex-grow" />

      <div className="flex items-center justify-between gap-2 self-stretch">
        <div>
          {props.initialValues?.key && props.initialValues?.namespace && (
            <Button
              type="button"
              variant="outlined"
              color="danger"
              loading={deleteItem.isMutating}
              onClick={() => {
                if (
                  props.initialValues?.key &&
                  props.initialValues?.namespace
                ) {
                  deleteItem.trigger({
                    key: props.initialValues?.key,
                    namespace: props.initialValues?.namespace,
                  });
                }
              }}
            >
              Delete
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="outlined"
            loading={putItem.isMutating}
            disabled={!form.formState.isDirty}
            onClick={() => form.reset(props.initialValues)}
          >
            Reset
          </Button>
          <Button type="submit" loading={putItem.isMutating}>
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

const InnerFolder = (props: {
  name: string;
  path: string[];
  view: View;
  contents: FolderContents;
  onCreate: (value: Partial<EditItem>) => void;
  onSelect: (value: EditItem) => void;
}) => {
  const [filter, setFilter] = useState<Filter>({});
  return (
    <Collapsible>
      <FolderTrigger
        name={props.name}
        count={props.contents.items.length}
        onClick={() => props.onCreate({ namespace: props.path })}
        actions={<FolderFilterDropdown value={filter} onChange={setFilter} />}
      />
      <CollapsibleContent className="ml-5 mt-1 flex flex-col gap-1">
        {Object.entries(props.contents.folders).map(([name, value]) => (
          <InnerFolder
            key={name}
            name={name}
            contents={value}
            path={[...props.path, name]}
            view={props.view}
            onSelect={props.onSelect}
            onCreate={props.onCreate}
          />
        ))}
        {filterItems(props.contents.items, filter)?.map((item) => (
          <File
            key={item.key}
            name={item.key}
            onClick={() => props.onSelect(item)}
            selected={isFileSelected(props.view, item)}
            date={item.updated_at || item.created_at}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

type Filter = {
  key?: string;
  value?: Record<string, unknown>;
};

const FolderFilterDropdown = (props: {
  value: Filter;
  onChange: (value: Filter) => void;
}) => {
  const { value: filter, onChange: setFilter } = props;
  return (
    <Popover>
      <PopoverTrigger
        onClick={(e) => e.stopPropagation()}
        className="self-center"
      >
        <IconButton
          variant="plain"
          color="secondary"
          icon={FilterLinesIcon}
          label="Filter"
          className="bg-tertiary"
        />
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col p-0"
        align="start"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="py-0.5 text-sm text-tertiary">Filter by key</span>

            {!isEmptyKeyFilter(filter.key) && (
              <button
                type="button"
                className="-mr-1 flex items-center gap-2 rounded-md px-1 py-0.5 text-xs transition-colors hover:bg-tertiary"
                onClick={() => setFilter({ ...filter, key: undefined })}
              >
                <span>Clear</span>
              </button>
            )}
          </div>
          <Input
            value={filter.key ?? ''}
            onChange={(e) => setFilter({ ...filter, key: e.target.value })}
            placeholder="Key"
          />
        </div>

        <div className="h-px w-full bg-secondary" />

        <div className="flex flex-col pb-1 pt-3">
          <div className="flex items-center justify-between gap-2 px-4">
            <span className="py-0.5 text-sm text-tertiary">
              Filter by value
            </span>

            {!isEmptyValueFilter(filter.value) && (
              <button
                type="button"
                className="-mr-1 flex items-center gap-2 rounded-md px-1 py-0.5 text-xs transition-colors hover:bg-tertiary"
                onClick={() => setFilter({ ...filter, value: undefined })}
              >
                <span>Clear</span>
              </button>
            )}
          </div>
          <CodeData
            value={filter.value ?? {}}
            variant="plain"
            onChange={(value) => setFilter({ ...filter, value })}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const RootFolder = (props: {
  name: string;
  path: string[];
  view: View;
  onSelect: (value: EditItem) => void;
  onCreate: (value: Partial<EditItem>) => void;
}) => {
  const [open, setOpen] = useState(true);
  const docs = useGraphMemorySearch(open ? props.name : null);

  const [filter, setFilter] = useState<Filter>({});
  const contents = useMemo(() => {
    const result: FolderContents = {
      folders: {},
      items: [],
    };

    for (const item of docs.data ?? []) {
      let current = result;
      for (const part of item.namespace.slice(1)) {
        current.folders[part] ??= { folders: {}, items: [] };
        current = current.folders[part];
      }
      current.items.push(item);
    }

    return result;
  }, [docs.data]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <FolderTrigger
        name={props.name}
        count={contents.items.length}
        onClick={() => props.onCreate({ namespace: props.path })}
        loading={docs.isLoading}
        actions={<FolderFilterDropdown value={filter} onChange={setFilter} />}
      />
      <CollapsibleContent className="ml-5 mt-1 flex flex-col gap-1">
        {Object.entries(contents.folders).map(([key, contents]) => (
          <InnerFolder
            key={key}
            name={key}
            contents={contents}
            path={[...props.path, key]}
            view={props.view}
            onSelect={props.onSelect}
            onCreate={props.onCreate}
          />
        ))}
        {filterItems(contents.items, filter)?.map((item) => (
          <File
            key={item.key}
            name={item.key}
            onClick={() => props.onSelect(item)}
            selected={isFileSelected(props.view, item)}
            date={item.updated_at || item.created_at}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const GraphMemoryBrowser = (props: {
  view: View;
  setView: (view: View) => void;
}) => {
  const root = useGraphMemoryNamespaces();

  if (root.isLoading) return <LinearProgress />;
  if (!root.data?.length) {
    return (
      <div className="absolute inset-0 grid items-center justify-center">
        <EmptyState
          title="No items found"
          description="Create a new item to get started"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 px-2 pb-3">
      {root.data?.map(([key]) => (
        <RootFolder
          path={[key]}
          key={key}
          name={key}
          view={props.view}
          onSelect={(value) => props.setView({ type: 'edit', value })}
          onCreate={(value) => props.setView({ type: 'create', value })}
        />
      ))}
    </div>
  );
};

function GraphMemoryEdit(props: { view: View; onClose: () => void }) {
  const [view, setView] = useState(props.view);

  return (
    <DialogRoot open onOpenChange={(open) => !open && props.onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="absolute inset-0 z-40 grid items-center justify-center overflow-auto bg-black/50">
          <div className="my-16 flex max-h-[calc(100vh-64px)] min-h-[65vh] w-[calc(100vw-32px)] max-w-[1280px] flex-col overflow-y-auto rounded-xl border border-secondary bg-popover shadow-lg [scrollbar-width:thin]">
            <PanelGroup direction="horizontal" className="flex-grow">
              <Panel
                minSize={30}
                defaultSize={35}
                className="flex min-w-[300px] flex-col rounded-l-xl bg-secondary"
              >
                <h2 className="p-4 pb-2 text-xl font-semibold tracking-tighter">
                  Memory
                </h2>

                <div className="relative flex-grow">
                  <div className="absolute inset-0 overflow-auto [scrollbar-width:thin]">
                    <GraphMemoryBrowser view={view} setView={setView} />
                  </div>
                </div>

                <div className="flex flex-col p-4">
                  <Button
                    type="button"
                    variant="outlined"
                    color="neutral"
                    className="flex items-center gap-2"
                    onClick={() => {
                      setView({ type: 'create', value: {} });
                    }}
                  >
                    <PlusIcon className="size-4" />
                    <span>Add new item</span>
                  </Button>
                </div>
              </Panel>

              <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-brand-green-400">
                <div className="border-l border-secondary" />

                <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
                <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all group-hover:opacity-100" />
              </PanelResizeHandle>

              <Panel className="relative min-w-[300px]">
                <div className="absolute inset-0 flex flex-col gap-2 overflow-auto [scrollbar-width:thin]">
                  <GraphMemoryForm
                    key={[view.type, view.value?.namespace, view.value?.key]
                      .filter(Boolean)
                      .join('/')}
                    initialValues={view.value}
                    onClose={props.onClose}
                    onSuccess={(type) => {
                      if (type != null) {
                        setView({ type: 'edit', value: type });
                      } else {
                        setView({ type: 'create', value: {} });
                      }
                    }}
                  />
                </div>
              </Panel>
            </PanelGroup>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}

export function GraphMemorySelect() {
  const [view, setView] = useState<
    | { type: 'create'; value: Partial<EditItem> }
    | { type: 'edit'; value: EditItem }
    | null
  >(null);

  return (
    <>
      <button
        onClick={() => setView({ type: 'create', value: {} })}
        type="button"
        className="flex min-h-[38px] items-center justify-between gap-2 rounded-md border border-secondary bg-transparent p-1.5 px-2.5 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-secondary [&>span]:line-clamp-1"
      >
        Memory
      </button>
      {view && <GraphMemoryEdit view={view} onClose={() => setView(null)} />}
    </>
  );
}
